import { DictionaryRecordContextProvider } from "@/components/dictionaries/DictionaryContext";
import DictionaryPageHeader from "@/components/dictionaries/DictionaryPageHeader";
import { useToast } from "@/components/ui/use-toast";
import { Module } from "@/constants";
import { useFormUnsavedChangesWarning } from "@/hooks/useFormUnsavedChangesWarning";
import { useQueryCacheUpdater } from "@/hooks/useQueryCacheUpdater";
import ShiftForm from "@/modules/shifts/components/ShiftForm";
import { useShiftDictionary } from "@/modules/shifts/hooks/useShiftDictionary";
import { ApiError } from "@/services/api";
import { ReadShift, WriteShift } from "@/types/shift";
import { zodResolver } from "@hookform/resolvers/zod";
import { MutationFunction, useMutation } from "@tanstack/react-query";
import { exists } from "i18next";
import { DefaultValues, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";

export default function ShiftCreator() {
  const id = "new";
  const { t } = useTranslation();
  const config = useShiftDictionary();
  const navigate = useNavigate();
  const { toast } = useToast();
  const labels = config.getLabels(t);
  const { invalidateListQuery } = useQueryCacheUpdater(Module.Shifts);
  const { can_create } = useModulePermissions();
  const mutationFn: MutationFunction<ReadShift, WriteShift> = async (
    values,
  ) => {
    const response = await config.service.post(values);
    return response;
  };

  const mutation = useMutation<ReadShift, ApiError, WriteShift>({
    mutationFn,
    onSuccess: (response) => {
      const id = response.id;
      navigate(`${config.path}/${id}`);
      toast({
        title: labels.success.create,
      });
      invalidateListQuery();
    },
    onError: ({ errorCode }) => {
      const description = exists(`errors.${errorCode}`)
        ? t(`errors.${errorCode}`)
        : t("errors.unknownError");
      toast({
        title: labels.errors.create,
        description,
        variant: "destructive",
      });
    },
  });

  const form = useForm<WriteShift>({
    resolver: zodResolver(config.schema),
    defaultValues: (config.defaultValues || {}) as DefaultValues<WriteShift>,
    disabled: mutation.isPending || !can_create,
  });

  useFormUnsavedChangesWarning(form);

  return (
    <DictionaryRecordContextProvider
      value={{
        config,
        mutation,
        form,
        id,
      }}
    >
      <DictionaryPageHeader />
      <ShiftForm />
    </DictionaryRecordContextProvider>
  );
}
